/**
 * The postMessage protocol between the game and the operator's lobby.
 *
 * The game runs in an <iframe> on the operator's page. The two documents
 * are on different origins, so postMessage is the only channel — and it is
 * a channel any page on the internet can also shout into, which shapes
 * everything below:
 *
 *   - we never post to `*`. A targetOrigin of "*" hands the balance and
 *     round history to whoever happens to have framed us, including a page
 *     that framed us without asking;
 *   - we never act on a message whose origin is not in the allowlist. The
 *     allowlist is build-time config (VITE_OPERATOR_ORIGINS), not something
 *     the page can talk us into;
 *   - inbound commands can only do what the player could do anyway (mute,
 *     re-read the balance). Nothing here spends money — a spin goes through
 *     the server with the session token, and the operator's lobby is not
 *     the authority on when to place a bet.
 *
 * Outside an iframe every function here is a no-op, so the standalone demo
 * is unaffected.
 */

import { config } from '../config'
import { log } from '../log'

/** Marks our frames apart from the other traffic on the channel. */
const SOURCE = 'slot-game'
/** Bumped only on a breaking change; operators pin against it. */
const PROTOCOL_VERSION = 1

export interface RoundEndPayload {
  roundId: string
  gameId: string
  betCoins: number
  winCoins: number
  balanceCoins: number
  currency: string
  capped: boolean
}

export type OutboundEvent =
  /** The game is on screen and playable. Always the first event. */
  | { type: 'gameLoaded'; gameId: string | null; currency: string; balanceCoins: number }
  /** Any balance change, whatever caused it (spin, gamble, refresh). */
  | { type: 'balanceUpdate'; balanceCoins: number; currency: string }
  /** A round has been charged and is playing out. */
  | { type: 'roundStart'; gameId: string; betCoins: number }
  /** The round is settled and the balance is final. */
  | { type: 'roundEnd'; round: RoundEndPayload }
  /** The player pressed exit. Closing the iframe is the operator's call. */
  | { type: 'exitRequest' }
  /** Something the player can see went wrong. Diagnostics, not control flow. */
  | { type: 'error'; message: string }

interface InboundHandlers {
  /** Mute/unmute everything — the lobby moved the game out of focus. */
  setMuted: (muted: boolean) => void
  /** Re-read the balance from the server: the operator credited the account. */
  refreshBalance: () => void
}

/**
 * Messages emitted before the parent origin is known.
 *
 * `gameLoaded` fires as soon as boot finishes, which can easily be before
 * the lobby's handshake arrives. Dropping it would leave an operator that
 * handshakes late permanently waiting for an event that already happened.
 * The cap is there so a page that never handshakes does not grow a queue
 * for the whole session.
 */
const MAX_QUEUED = 16

let parentOrigin: string | null = null
let handlers: InboundHandlers | null = null
const queue: OutboundEvent[] = []

/** True when we are framed at all. A top-level page has nobody to talk to. */
function isEmbedded(): boolean {
  try {
    return window.parent !== window
  } catch {
    // A cross-origin parent never throws on this comparison, but a sandboxed
    // frame can throw on almost anything. Assume embedded and let the
    // allowlist decide.
    return true
  }
}

function isAllowed(origin: string): boolean {
  return config.operatorOrigins.includes(origin)
}

/**
 * The operator's origin, if we can establish it before the handshake.
 *
 * `document.referrer` is the framing page for an iframe's initial load. It
 * is not a security boundary — Referrer-Policy can strip it and a hostile
 * page cannot forge it into the allowlist anyway — so it is only ever used
 * as a hint that still has to pass `isAllowed`.
 */
function referrerOrigin(): string | null {
  if (!document.referrer) return null
  try {
    return new URL(document.referrer).origin
  } catch {
    return null
  }
}

function post(event: OutboundEvent): void {
  if (!parentOrigin) return
  window.parent.postMessage({ source: SOURCE, version: PROTOCOL_VERSION, ...event }, parentOrigin)
}

function flush(): void {
  while (queue.length > 0) post(queue.shift() as OutboundEvent)
}

function onMessage(e: MessageEvent): void {
  const data = e.data as { source?: unknown; type?: unknown; muted?: unknown } | null
  const looksLikeHost =
    data !== null && typeof data === 'object' && data.source === `${SOURCE}-host`

  if (!isAllowed(e.origin)) {
    // The single most useful line in an integration that "just does not
    // work": the operator is talking to us from an origin nobody put in the
    // allowlist. Only for frames that claim to be a host — the channel is
    // full of unrelated widget traffic and logging all of it is noise.
    if (looksLikeHost) {
      log.warn(
        'bridge.origin-rejected',
        'a host message came from an origin outside VITE_OPERATOR_ORIGINS',
        { origin: e.origin, allowed: config.operatorOrigins },
      )
    }
    return
  }
  // Our own outbound frames bounce back in some embedding setups, and the
  // page hosts third-party widgets with their own postMessage traffic.
  if (!looksLikeHost) return

  // The first allowed message pins the origin: an operator that handshakes
  // is authoritative over the referrer hint.
  parentOrigin = e.origin
  flush()

  switch (data.type) {
    case 'handshake':
      // Nothing to do beyond the pinning above — the queued gameLoaded is
      // already on its way out via flush().
      break
    case 'setMuted':
      if (typeof data.muted === 'boolean') handlers?.setMuted(data.muted)
      break
    case 'refreshBalance':
      handlers?.refreshBalance()
      break
    default:
      // An unknown command from a known operator is a version mismatch, not
      // an attack. Ignoring it keeps an older client working against a newer
      // lobby — but it is also why an operator swears they sent something.
      log.info('bridge.unknown-command', 'ignored a command this client does not implement', {
        command: String(data.type),
        protocolVersion: PROTOCOL_VERSION,
      })
      break
  }
}

/**
 * Starts the bridge. Safe to call when not embedded — it does nothing.
 *
 * Called once from the store's boot, before the first event is emitted.
 */
export function startBridge(h: InboundHandlers): void {
  if (!isEmbedded()) return
  handlers = h

  const hint = referrerOrigin()
  if (hint && isAllowed(hint)) parentOrigin = hint

  if (config.operatorOrigins.length === 0) {
    // Framed with an empty allowlist: every event will be queued and then
    // dropped, and the operator will report a game that "does not talk". The
    // build is missing VITE_OPERATOR_ORIGINS.
    log.warn(
      'bridge.no-operators',
      'the game is framed but no operator origins were configured at build time',
      { referrer: hint },
    )
  } else if (!parentOrigin) {
    log.info('bridge.awaiting-handshake', 'the framing origin is not known yet', {
      referrer: hint,
      allowed: config.operatorOrigins,
    })
  }

  window.addEventListener('message', onMessage)
  flush()
}

/** Emits an event, or queues it until the operator's origin is known. */
export function emit(event: OutboundEvent): void {
  if (!isEmbedded()) return
  if (parentOrigin) {
    post(event)
    return
  }
  if (queue.length >= MAX_QUEUED) {
    // Nobody handshaked and the game kept playing. The operator has lost
    // events for good, so this is the moment it becomes a fact rather than
    // a suspicion.
    log.warn('bridge.queue-overflow', 'an event was dropped before any handshake arrived', {
      dropped: (queue[0] as OutboundEvent).type,
      pending: queue.length,
    })
    queue.shift()
  }
  queue.push(event)
}

/**
 * Whether we are running inside a frame at all.
 *
 * The UI uses this to decide who owns "leave the game": framed, the lobby
 * belongs to the operator and all we can do is ask to be closed. This is
 * deliberately NOT gated on the allowlist — the answer must be stable from
 * the first paint, while `parentOrigin` may only be pinned once the
 * operator handshakes, and an exit button that changes meaning halfway
 * through the session is worse than one that is occasionally inert.
 */
export function isFramed(): boolean {
  return isEmbedded()
}
